import React, { useState } from 'react';
import { Layers, Download, Info, FileText, TrendingUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения подробной разбивки стоимости архитектурного проекта
 */
const ProjectBreakdown = ({ breakdown, results, showDetailedBreakdown, setShowDetailedBreakdown, formData }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!breakdown || breakdown.length === 0 || !results) {
    return null;
  }

  // Получение символа валюты
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return '₽';
    }
  };

  const currencySymbol = getCurrencySymbol(formData.currencyType);

  // Для отображения только основных элементов, если не показана полная разбивка
  const displayBreakdown = showDetailedBreakdown 
    ? breakdown 
    : breakdown.filter(item => item.essential || item.amount > results.totalCost * 0.1);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-indigo-500" />
          Разбивка стоимости проекта
        </h3>

        {/* Кнопка переключения полной/краткой разбивки */}
        {breakdown.length > displayBreakdown.length && (
          <Button
            size="sm"
            onClick={() => setShowDetailedBreakdown(prev => !prev)}
            variant="outline"
            aria-expanded={showDetailedBreakdown}
          >
            {showDetailedBreakdown ? 'Показать основные элементы' : 'Показать полную разбивку'}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий обзор
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'detailed'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Расчет стоимости
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showDetailedBreakdown 
            ? "Полная разбивка стоимости проекта по всем составляющим. Включает базовые и дополнительные услуги." 
            : "Показаны основные элементы стоимости проекта. Нажмите 'Показать полную разбивку' для детального расчета."
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] project-breakdown-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[600px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold">Наименование</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Стоимость, {currencySymbol}</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Доля, %</th>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold">Пояснение</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Стоимость, {currencySymbol}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayBreakdown.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
              >
                <td className="px-3 py-1.5 whitespace-nowrap font-medium">{item.name}</td>

                {activeTab === 'general' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                      {item.amount.toLocaleString('ru-RU')}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {((item.amount / results.totalCost) * 100).toFixed(1)}%
                    </td>
                  </>
                )}

                {activeTab === 'detailed' && (
                  <>
                    <td className="px-3 py-1.5">{item.description}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                      {item.amount.toLocaleString('ru-RU')}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap">ИТОГО:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{results.totalCost.toLocaleString('ru-RU')}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">100%</td>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap">Полная стоимость проекта</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(results.totalCost, 0)}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Дополнительная информация */}
      <div className="mt-5 bg-gray-50 dark:bg-gray-800/70 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Примечания к смете</h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5">
              <li className="flex items-start">
                <TrendingUp className="w-4 h-4 text-indigo-500 mt-0.5 mr-1.5 flex-shrink-0" />
                <span>Срок выполнения: <strong>{results.timelineWeeks} {formatWeeksLabel(results.timelineWeeks)}</strong></span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-5 h-5 text-center leading-5 text-white bg-indigo-500 rounded-full text-xs font-bold mr-1.5 flex-shrink-0">%</span>
                <span>Стоимость указана с учетом НДС 20%</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-5 h-5 text-center leading-5 text-white bg-indigo-500 rounded-full text-xs font-bold mr-1.5 flex-shrink-0">i</span>
                <span>Данная смета является предварительной и может корректироваться после детального обсуждения проекта</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Кнопка показа полной разбивки (если она скрыта) */}
      {!showDetailedBreakdown && breakdown.length > displayBreakdown.length && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowDetailedBreakdown(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать полную разбивку стоимости
          </Button>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слова "неделя" в зависимости от числа
const formatWeeksLabel = (weeks) => {
  const lastDigit = weeks % 10;
  const lastTwoDigits = weeks % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'недель';
  if (lastDigit === 1) return 'неделя';
  if (lastDigit >= 2 && lastDigit <= 4) return 'недели';
  return 'недель';
};

export default ProjectBreakdown;